/**
 * Partner Attribution API client — Phase 7.
 * Thin wrappers around GET/PUT /v1/partner/attribution.
 * Uses Firebase ID token auth; unwraps TransformInterceptor envelope.
 */

import { API_BASE } from '@/src/infrastructure/lib/api';
import { auth } from '@/src/infrastructure/config/firebase';
import type { AttributionSettings, UpdateAttributionPayload } from './types';

export type CreateProfilePayload = {
  businessName: string;
  partnerType: string;
  ownerName?: string;
  phone?: string;
};

async function authFetch<T>(url: string, init: RequestInit = {}): Promise<T> {
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.message ?? `HTTP ${res.status}`);
  }
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

export function createProfile(payload: CreateProfilePayload): Promise<unknown> {
  return authFetch<unknown>(`${API_BASE}/partner/profile`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export type UpdateProfilePayload = {
  businessName?: string;
  partnerType?: string;
  ownerName?: string | null;
  legalName?: string | null;
  phone?: string | null;
  websiteUrl?: string | null;
  googleMapsUrl?: string | null;
  logoUrl?: string | null;
};

export function updateProfile(payload: UpdateProfilePayload): Promise<unknown> {
  return authFetch<unknown>(`${API_BASE}/partner/profile`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}


export function fetchAttribution(): Promise<AttributionSettings> {
  return authFetch<AttributionSettings>(`${API_BASE}/partner/attribution`);
}

export function updateAttribution(payload: UpdateAttributionPayload): Promise<AttributionSettings> {
  return authFetch<AttributionSettings>(`${API_BASE}/partner/attribution`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}
